import HelpIcon from '@mui/icons-material/Help';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	IconButton,
	Paper,
	Typography, 
	Grid
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import connectionService from '../services/Connection';
import Model from '../models/model';
import consts from '../consts';
import strings from '../Globalization/index';
import LoadingDialog from '../components/common/LoadingDialog';
import { useModelForm } from '../contexts/ModelForm';

dayjs.extend(utc);

const loadTasks = async (deps, context) => {
	const { EquipmentId } = deps;
	const formData = new FormData();
	formData.append('asArray', '0');
	formData.append('parentField', '');
	formData.append('comboType', 'TaskTypeName');
	if (EquipmentId) {
		formData.append('parentId', EquipmentId);
	}
	const { options, count } = await fetch(connectionService.Combo, {
		method: 'POST',
		body: formData
	})
		.then((res) => res.json())
		.then((json) => {
			const { records } = json;
			let options = records?.map((record) => ({
				id: record.LookupId,
				label: record.DisplayValue
			}));
			if (!Array.isArray(options)) {
				options = [];
			}
			return { options: options || [], count: options?.length };
		});
	return { options, count };
};

const customizeSaveData = (toSave, { evaluative }) => {
	let TaskScheduleStart = dayjs(toSave.TaskScheduleStart);
	let TaskScheduleStartUTC = dayjs(toSave.TaskScheduleStartUTC).utc();
	let PastDueOnUTC = dayjs(toSave.PastDueOnUTC);
	let PastDueOn = dayjs(toSave.PastDueOn);
	let DueOnUTC = dayjs(toSave.DueOnUTC);
	let DueOn = dayjs(toSave.DueOn);

	if (toSave.TaskScheduleType === 'M') {
		toSave.DailyDaysSchedule = '';
		toSave.Days = '';
		toSave.DailyScheduleType = '';
		toSave.WeekRecurEvery = '';
		toSave.DailyScheduleType = '';
		if (toSave.MonthlyScheduleType === '1') {
			toSave.MonthlyScheduleTheDayNumberId = '';
			toSave.MonthlyScheduleTheDayId = '';
		} else {
			toSave.MonthlyScheduleDayDate = '';
		}
	} else {
		if (toSave.TaskScheduleType === 'D') {
			if (toSave.DailyScheduleType === '2') {
				toSave.Days = '';
			}
			toSave.WeekRecurEvery = '';
		} else {
			toSave.Days = '';
			toSave.DailyDaysSchedule = '';
		}
		toSave.MonthlyScheduleType = '';
		toSave.MonthlyScheduleDayDate = '';
		toSave.MonthlyScheduleDayMonth = '';
		toSave.MonthlyScheduleTheDayNumberId = '';
		toSave.MonthlyScheduleTheDayId = '';
		toSave.MonthlyScheduleTheMonth = '';
	}
	if (toSave.TaskScheduleType !== 'W') {
		toSave.Custom = '0000000';
		toSave.WeekRecurEvery = '';
	}
	if (TaskScheduleStart.isValid()) {
		TaskScheduleStart = TaskScheduleStart.clone().hour(DueOn.hour()).minute(DueOn.minute());
		if (evaluative) {
			toSave.NextOccurence = TaskScheduleStart.clone().format(consts.MomentFormat.DateTimeFormat);
			toSave.NextOccurenceUTC = TaskScheduleStart.clone().utc().format(consts.MomentFormat.DateTimeFormat);
		} else if (toSave.NextOccurence) {
			toSave.NextOccurence = dayjs(toSave.NextOccurence).format(consts.MomentFormat.DateTimeFormat);
			toSave.NextOccurenceUTC = dayjs(toSave.NextOccurence).utc().format(consts.MomentFormat.DateTimeFormat);
		}
		toSave.TaskScheduleStart = TaskScheduleStart.format(consts.MomentFormat.DateTimeFormat);
		toSave.TaskScheduleStartUTC = TaskScheduleStart.utc().format(consts.MomentFormat.DateTimeFormat);
	}
	if (TaskScheduleStartUTC.isValid()) {
		// for future use
		//TaskScheduleStartUTC = TaskScheduleStartUTC.clone().hour(DueOnUTC.hour()).minute(DueOnUTC.minute());
		//toSave.TaskScheduleStartUTC = TaskScheduleStartUTC.format(consts.MomentFormat.DateTimeFormat);
	}
	if (DueOn.isValid()) {
		DueOn = DueOn.clone().date(TaskScheduleStart.date()).month(TaskScheduleStart.month()).year(TaskScheduleStart.year());
		toSave.DueOn = DueOn.format(consts.MomentFormat.DateTimeFormat);
		toSave.DueOnUTC = DueOn.utc().format(consts.MomentFormat.DateTimeFormat);
	}
	if (PastDueOn.isValid()) {
		PastDueOn = PastDueOn.clone()
			.date(TaskScheduleStart.date())
			.month(TaskScheduleStart.month())
			.year(TaskScheduleStart.year());
		toSave.PastDueOn = PastDueOn.format(consts.MomentFormat.DateTimeFormat);
		toSave.PastDueOnUTC = PastDueOn.utc().format(consts.MomentFormat.DateTimeFormat);
	}

	if (!toSave.AssignedToUserId) {
		toSave.AssignedToUserId = 0;
	}
	return toSave;
};

const helpRenderer = ({ row = {} }) => {
	const attachmentCount = row.AttachmentCount;
	return (
		attachmentCount !== 0 ? (
			<IconButton
				color="primary"
				component={Link}
				to={encodeURI(
					`/cycleattachmentlist?AssociationId=${row.id}&AttachmentTypeId=${consts.Task.AssociationType}&AssociationTypeId=${consts.Task.AttachmentType}&attachment=${strings.TaskAssignment}&isFromScreen=false&Help=${row.Help}`
				)}
			>
				<HelpIcon fontSize="inherit" />
			</IconButton>
		) : (
			<Button
				component={Link}
				size="small"
				variant="contained"
				to={encodeURI(
					`/cycleattachmentlist?AssociationId=${row.id}&AttachmentTypeId=${consts.Task.AssociationType}&AssociationTypeId=${consts.Task.AttachmentType}&attachment=${strings.TaskAssignment}&isFromScreen=false&Help=${row.Help}`
				)}
			>
				N/A
			</Button>
		)
	)
}

const ScheduleFormGroup = ({ header, children }) => {
	const { formik, getValidationErrorsMsg } = useModelForm()
	const [loading, setLoading] = useState(false);
	const handleOnClickNextOccurrence = useCallback(async () => {
		const validationErrorsMsg = await getValidationErrorsMsg();
		if (validationErrorsMsg) {
			try {
				formik.validateForm();

				Swal.fire({
					titleText: strings.Info,
					html: `<p>${strings.NextOccurenceError}</p>${validationErrorsMsg || ''}`,
					icon: 'error',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
			} catch (error) {
				console.error('error', error);
			}
		} else {
			setLoading(true);
			const {
				TaskScheduleType,
				TaskScheduleStartUTC,
				TaskScheduleStart,
				DailyDaysSchedule,
				DailyScheduleType,
				WeekRecurEvery,
				Custom,
				MonthlyScheduleType,
				MonthlyScheduleDayDate,
				MonthlyScheduleDayMonth,
				MonthlyScheduleTheDayNumberId,
				MonthlyScheduleTheDayId,
				MonthlyScheduleTheMonth,
				NextOccurence,
				NextOccurenceUTC,
				DueOn,
				DueOnUTC,
				SkipHolidays
			} = customizeSaveData(Object.assign({}, JSON.parse(JSON.stringify(formik.values))), { evaluative: true });
			const data = {
				TaskScheduleType,
				TaskScheduleStartUTC,
				TaskScheduleStart,
				DailyDaysSchedule,
				DailyScheduleType,
				WeekRecurEvery,
				Custom,
				MonthlyScheduleType,
				MonthlyScheduleDayDate,
				MonthlyScheduleDayMonth,
				MonthlyScheduleTheDayNumberId,
				MonthlyScheduleTheDayId,
				MonthlyScheduleTheMonth,
				NextOccurence,
				NextOccurenceUTC,
				DueOn,
				DueOnUTC,
				SkipHolidays
			};
			const setNextOccurence = (nextDate) => {
				const NextDate = dayjs(nextDate).format(consts.MomentFormat.DateTimeFormat);
				formik.setFieldValue('NextOccurence', NextDate);
			};
			const formData = new FormData();
			for (const [key, value] of Object.entries(data)) {
				formData.append(key, value);
			}
			formData.append('action', 'otherAction');
			formData.append('otherAction', 'GetNextGenerationDateTime');
			fetch(connectionService.TaskAssignmentList, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then(({ success, nextDate }) => {
					if (success && nextDate) {
						setNextOccurence(nextDate);
					} else {
						Swal.fire({ titleText: strings.Info, html: strings.NextOccurenceError, icon: 'error', allowOutsideClick: false });
					}
				})
				.catch((err) => {
					console.error('err', err);
					Swal.fire({ titleText: strings.ErrorTitle, html: strings.Status_503, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				})
				.finally(() => setLoading(false));
		}
	}, [formik.values]);

	return (
		<Accordion
			disableGutters
			elevation={0}
			sx={{
				flex: 1,
				marginBottom: 5,
				'& .MuiAccordionDetails-root': { padding: (theme) => theme.spacing(2, 0) },
				'& .placeRight': {
					display: 'flex',
					flexDirection: 'row-reverse'
				},
				'& .placeBottom': {
					height: 'revert',
					display: 'flex',
					alignItems: 'flex-end',
					'& .innerInputWrapper': {
						height: 'fit-content'
					}
				}
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon color="inherit" />}
				size="small"
				sx={{
					backgroundColor: (theme) => theme.palette.primary.main,
					minHeight: 32,
					borderRadius: 1,
					color: (theme) => theme.palette.background.paper,
					'& .MuiAccordionSummary-content': {
						justifyContent: 'center',
						margin: (theme) => theme.spacing(0, 0)
					},
					'& .MuiAccordionSummary-expandIconWrapper': {
						color: (theme) => theme.palette.background.paper
					}
				}}
			>
				<Typography component="span">{header}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<LoadingDialog open={loading} />
				<Grid container>
					<Grid
						size={12}						
						container
						spacing={1}
						sx={{ padding: (theme) => theme.spacing(1, 2), '& .MuiGrid-root:empty': { display: 'none' } }}
					>
						{children}
					</Grid>
					<Grid size={12}>
						<Button
							variant="contained"
							onClick={handleOnClickNextOccurrence}
							disableElevation
						>
							{strings.NextOccurence}
						</Button>
					</Grid>
				</Grid>
			</AccordionDetails>
		</Accordion>
	);
};
const WeekCustom = ({ inputProps: { column, formik, field, fieldLabel } }) => {
	const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const values = (
		typeof formik.values[field] === 'string' && formik.values[field]?.length === 7 ? formik.values[field] : '0000000'
	).split('');
	const handleChange = useCallback(
		(i) => (event) => {
			let newValArr = JSON.parse(JSON.stringify(values));
			newValArr[i] = event.target.checked ? '1' : '0';
			formik.setFieldValue(field, newValArr.join(''), true);
		},
		[values]
	);
	return (
		<Box>
			<FormControl component="fieldset" variant="standard">
				<FormGroup row sx={{ '& .MuiPaper-elevation': { mr: 1, mb: 1, padding: (theme) => theme.spacing(0, 1) } }}>
					{weekDays.map((weekDay, i) => (
						<Paper elevation={1} key={i}>
							<FormControlLabel
								control={<Checkbox checked={values[i] === '1'} onChange={handleChange(i)} name={weekDay} />}
								label={weekDay}
							/>
						</Paper>
					))}
				</FormGroup>
				{formik.values[field] === '0000000' && (
					<FormHelperText component="span" error={formik.values[field] === '0000000'}>
						{strings.SelectWeek}
					</FormHelperText>
				)}
			</FormControl>
		</Box>
	);
};

const model = new Model({
	name: 'TaskAssignment',
	gridTitle: 'TaskAssignment',
	defaultSort: 'NextOccurence DESC',
	api: 'TaskAssignment',
	extraExportConfig: {
		PDF: {
			pageSize: 'A3'
		}
	},
	formTitleField: 'TaskName',
	headerFilterApplyTrigger: 'onChange',
	initialValues: {
		SkipHolidays: true,
		Custom: '0000000'
	},
	showGridRefreshBtn: true,
	disableDelete: ({ activeRecord }) => {
		return Boolean(activeRecord?.record?.IsAutoPopulated);
	},
	showDeleteBtn: true,
	combos: [
		{ type: 'User', loaded: true },
		{ type: 'WeekDays', loaded: true },
		{ type: 'DayNumber', loaded: true },
		{ type: 'TaskTypeName', loaded: true },
		{ type: 'Equipment', loaded: true }
	],
	customizeSaveData: customizeSaveData,

	formGroups: [
		{
			fields: [
				'EquipmentId',
				'TaskTypeId',
				'AssignedToUserId',
				'NextOccurence',
				'Help',
				'TaskDescription',
				'RecommendedFrequency',
				'IsNotify',
				'IsActive',
				'SkipHolidays'
			]
		},
		{
			fields: [
				'TaskScheduleStart',
				'DueOn',
				'PastDueOn',
				'TaskScheduleType',
				'DailyScheduleType',
				'MonthlyScheduleType',
				'WeekRecurEvery',
				'DailyDaysSchedule',
				'MonthlyScheduleDayDate',
				'Custom',
				'MonthlyScheduleTheDayNumberId',
				'MonthlyScheduleTheDayId',
				'MonthlyScheduleTheMonth'
			],
			header: 'Schedule',
			columnSize: 4,
			fieldProps: {
				inlineLabel: false
			},
			customizeFormGroup: ScheduleFormGroup
		}
	],
	columns: [
		{ header: 'UniqueId', field: 'TaskAssignmentId', keyColumn: true, type: 'string', width: 120, hidden: true },
		{
			field: 'equipmentId',
			header: 'EquipmentName',
			width: 175,
			lookup: 'Equipment',
			headerFilter: true,
			standaloneHeaderFilter: true,
			filterColSize: 5,
			isNotformfield: true
		},
		{
			field: 'EquipmentName',
			header: 'EquipmentName',
			width: 175,
			formField: 'EquipmentId',
			required: true,
			onChange: ({ formik }) => {
				const values = { ...formik.values }
				if (values.TaskTypeId) {
					delete values.TaskTypeId;
					formik.setValues(values);
				}
			},
			readOnly: ({ activeRecord }) => {
				return Boolean(activeRecord?.id);
			},
			lookup: 'Equipment',
			ignoreLookupOnGridFilter: true
		},
		{ field: 'TaskType', width: 160, isNotformfield: true },
		{
			header: 'Task',
			field: 'TaskName',
			formField: 'TaskTypeId',
			width: 200,
			lookup: loadTasks,
			dependsOn: ['EquipmentId'],
			readOnly: ({ activeRecord }) => {
				return Boolean(activeRecord?.id);
			},
			required: true,
			addable: ({ formik }) => `/tasktypelist?id=new&returnTo=/taskassignmentlist?id=${formik?.values?.id || 'new'}`,
			editable: ({ formik }) =>
				`/tasktypelist?id=${formik.values.TaskTypeId}&returnTo=/taskassignmentlist?id=${formik?.values?.id || 'new'
				}`,
			ignoreLookupOnGridFilter: true
		},
		{
			header: 'Assignee',
			field: 'AssignedToUser',
			formField: 'AssignedToUserId',
			width: 150,
			lookup: 'User',
			required: [['IsNotify', true]],
			ignoreLookupOnGridFilter: true
		},
		{
			field: 'NextOccurence',
			header: 'NextOccurence',
			width: 160,
			type: 'dateTime',
			disabled: true
		},
		{
			field: 'DueOn',
			label: 'DueOn',
			width: 100,
			type: 'time',
			filterable: false,
			inputProps: {
				ampmInClock: false
			},
			inlineLabelSize: 12,
			maxTime: ({ formik }) => (formik.values.PastDueOn ? dayjs(formik.values.PastDueOn) : undefined),
			onChange: ({ formik, value }) => {
				const DueOnUTC =
					value && value.isValid() ? value.clone().utc().format(consts.MomentFormat.DateTimeFormat) : null;
				formik.setFieldValue('DueOnUTC', DueOnUTC);
			},
			formFormat: consts.MomentFormat.DateTimeFormat,
			required: true
		},
		{
			field: 'PastDueOn',
			width: 130,
			type: 'time',
			filterable: false,
			inlineLabelSize: 12,
			label: 'PastDueOn',
			minTime: ({ formik }) =>
				formik.values.DueOn
					? dayjs(formik.values.DueOn)
					: formik.values.TaskScheduleStart
						? dayjs(formik.values.TaskScheduleStart)
						: undefined,
			onChange: ({ formik, value }) => {
				const PastDueOnUTC =
					value && value.isValid() ? value.clone().utc().format(consts.MomentFormat.DateTimeFormat) : null;
				formik.setFieldValue('PastDueOnUTC', PastDueOnUTC);
			},
			formFormat: consts.MomentFormat.DateTimeFormat,
			required: true
		},

		{ header: 'Notify', field: 'IsNotify', width: 130, type: 'bool' },
		{ header: 'Active', field: 'IsActive', width: 120, type: 'bool' },
		{ field: 'SkipHolidays', width: 140, type: 'bool' },
		{ field: 'TaskDescription', width: 350, hide: true, readOnly: true },
		{
			field: 'Help',
			width: 120,
			align: 'center',
			renderCell: helpRenderer,
			filterable: false,
			multiline: true,
			rows: 2,
			maxLength: 250
		},
		{ field: 'RecommendedFrequency', width: 200, readOnly: true },
		{
			header: 'StartDate',
			field: 'TaskScheduleStart',
			width: 0,
			type: 'date',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			hide: true,
			minDate: () => dayjs().utc().local(),
			format: consts.MomentFormat.MonthDateYear,
			filterable: false,
			required: true
		},

		{
			field: 'TaskScheduleType',
			width: 80,
			type: 'string',
			required: true,
			inlineLabelSize: 12,
			excludeOnGrid: true,
			hide: true,
			options: [
				{ value: 'D', label: 'Daily' },
				{ value: 'W', label: 'Weekly' },
				{ value: 'M', label: 'Monthly' }
			]
		},
		{
			field: 'DailyScheduleType',
			width: 80,
			hide: true,
			excludeOnGrid: true,
			inlineLabelSize: 12,
			required: [['TaskScheduleType', 'D']],
			inputWhen: [{ field: 'TaskScheduleType', value: 'D' }],
			options: [
				{ value: '1', label: 'Every' },
				{ value: '2', label: 'Every Weekday' }
			]
		},

		{
			header: 'Days',
			field: 'DailyDaysSchedule',
			width: 10,
			hide: true,
			type: 'number',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			minValue: 1,
			maxValue: 365,
			required: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'D' },
				{ field: 'DailyScheduleType', op: 'eq', value: '1' }
			],
			inputWhen: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'D' },
				{ field: 'DailyScheduleType', op: 'eq', value: '1' }
			],
			min: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'D' },
				{ field: 'DailyScheduleType', op: 'eq', value: '1' }
			],
			max: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'D' },
				{ field: 'DailyScheduleType', op: 'eq', value: '1' }
			]
		},
		{
			field: 'MonthlyScheduleType',
			header: 'ScheduleType',
			inlineLabelSize: 12,
			width: 80,
			excludeOnGrid: true,
			inputWhen: [{ field: 'TaskScheduleType', op: 'eq', value: 'M' }],
			required: [{ field: 'TaskScheduleType', op: 'eq', value: 'M' }],
			options: [
				{ value: '1', label: 'Day' },
				{ value: '2', label: 'The' }
			]
		},
		{
			header: 'Days',
			field: 'MonthlyScheduleDayDate',
			width: 10,
			hide: true,
			type: 'number',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			inputWrapperProps: {
				className: 'placeRight'
			},
			minValue: 1,
			maxValue: 31,
			min: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '1' }
			],
			max: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '1' }
			],
			required: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '1' }
			],
			inputWhen: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '1' }
			],
			innerInputWrapperProps: {
				size: 4,
				sx: {
					paddingLeft: (theme) => theme.spacing(0.5)
				}
			},
			columnSize: 12
		},

		{
			field: 'WeekRecurEvery',
			header: 'RecurEvery',
			width: 80,
			type: 'number',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			minValue: 1,
			maxValue: 52,
			inputWhen: [{ field: 'TaskScheduleType', op: 'eq', value: 'W' }],
			required: [{ field: 'TaskScheduleType', op: 'eq', value: 'W' }],
			min: [{ field: 'TaskScheduleType', op: 'eq', value: 'W' }],
			max: [{ field: 'TaskScheduleType', op: 'eq', value: 'W' }]
		},
		{
			field: 'Custom',
			header: 'WeekOn',
			inlineLabelSize: 12,
			hideLabel: true,
			excludeOnGrid: true,
			required: [['TaskScheduleType', 'W']],
			inputWhen: [['TaskScheduleType', 'W']],
			customizeInput: WeekCustom,
			validate: yup.string().test({
				name: 'is-in-bounds',
				message: 'Select any week day',
				test: (value, ctx) => {
					if (ctx?.parent?.TaskScheduleType === 'W') {
						return value !== '0000000';
					}
					return true;
				}
			})
		},

		{
			header: 'Week',
			field: 'MonthlyScheduleTheDayNumberId',
			lookup: 'DayNumber',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			columnSize: 12,
			required: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '2' }
			],
			inputWhen: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '2' }
			],
			inputWrapperProps: {
				className: 'placeRight'
			},
			innerInputWrapperProps: {
				size: 4,
				sx: {
					paddingLeft: (theme) => theme.spacing(0.5)
				}
			}
		},
		{
			header: 'WeekDay',
			field: 'MonthlyScheduleTheDayId',
			inlineLabelSize: 12,
			excludeOnGrid: true,
			lookup: 'WeekDays',
			columnSize: 12,
			required: [['MonthlyScheduleType', '2']],
			inputWhen: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'eq', value: '2' }
			],
			inputWrapperProps: {
				className: 'placeRight'
			},
			innerInputWrapperProps: {
				size: 4,
				sx: {
					paddingLeft: (theme) => theme.spacing(0.5)
				}
			}
		},
		{
			field: 'MonthlyScheduleTheMonth',
			type: 'number',
			inlineLabelSize: 12,
			header: 'EveryMonth',
			excludeOnGrid: true,
			columnSize: 12,
			minValue: 1,
			maxValue: 12,
			min: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'in', value: ['1', '2'] }
			],
			max: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'in', value: ['1', '2'] }
			],
			required: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'in', value: ['1', '2'] }
			],
			inputWhen: [
				{ field: 'TaskScheduleType', op: 'eq', value: 'M' },
				{ field: 'MonthlyScheduleType', op: 'in', value: ['1', '2'] }
			],

			inputWrapperProps: {
				className: 'placeRight'
			},
			innerInputWrapperProps: {
				size: {lg: 4},
				sx: {
					paddingLeft: (theme) => theme.spacing(0.5)
				}
			}
		}
	]
});

export default model.createFormBase();
